import './App.css';
import './components/RegisterSkeleton.css';
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import AppCredit from './components/AppCredit';
import Arabesque from './components/Arabesque';
import Arabesque2 from './components/Arabesque2';
import Arabesque3 from './components/Arabesque3';
import Menu from './components/Menu';
import MenuFilter from './components/MenuFilter';
import LibraryImportNav from './components/LibraryImportNav';
import MenuMiddle from './components/MenuMiddle';
import MenuNotesMiddle from './components/MenuNotesMiddle';
import MenuGroupingsMiddle from './components/MenuGroupingsMiddle';
import MenuNotesNav from './components/MenuNotesNav';
import MenuIconTooltip from './components/MenuIconTooltip';
import { Save, X } from 'lucide-react';
import NotesSpreadFontDropdown from './components/NotesSpreadFontDropdown';
import { MENU_NOTES_TOOLTIPS } from './constants/menuTooltips';
import {
  GroupingCreatePanel,
  GroupingSuccessPanel,
  GroupingsListPanel,
} from './components/GroupingsView';
import GroupingDetailPanel from './components/GroupingDetailPanel';
import GroupingConfirmModal from './components/GroupingConfirmModal';
import GroupingRemoveWordsModal from './components/GroupingRemoveWordsModal';
import GroupingReassignWordsModal from './components/GroupingReassignWordsModal';
import {
  createGroupement,
  deleteGroupement,
  emptyGroupement,
  fetchGroupementMots,
  fetchGroupementsCatalog,
  reassignWordsFromGroupement,
  removeWordsFromGroupement,
  updateGroupement,
} from './utils/groupementsApi';
import {
  NotesDetailPanel,
  NotesListPanel,
  NotesWorkspaceProvider,
} from './components/NotesWorkspace';
import MenuLeft from './components/MenuLeft';
import SearchBar from './components/SearchBar';
import QuestJournalSearchScope from './components/QuestJournalSearchScope';
import WordListBox from './components/WordListBox';
import WordDetailsPanel from './components/WordDetailsPanel';
import WordEditPanel from './components/WordEditPanel';
import LastGameStatsPanel from './components/LastGameStatsPanel';
import RegisterFilteredStatsPanel from './components/RegisterFilteredStatsPanel';
import RegisterRecencyPanel from './components/RegisterRecencyPanel';
import { readDeepLinkedWord } from './utils/registerWordLink';
import { readCaptureLink } from './utils/captureLink';
import {
  readRegisterRecencyStratum,
  writeRegisterRecencyStratum,
} from './utils/registerRecencyPreference';
import CaptureDeMot from './components/CaptureDeMot';
import EntrainementVoc from './components/EntrainementVoc';
import GameQCM from './components/GameQCM';
import GameStatsView from './components/GameStatsView';
import ControlPanelView from './components/ControlPanelView';
import MenuCpanelNav from './components/MenuCpanelNav';
import GemCollectionView from './components/GemCollectionView';
import MenuCollectionNav from './components/MenuCollectionNav';
import MenuMasteryNav from './components/MenuMasteryNav';
import MenuStatsNav from './components/MenuStatsNav';
import {
  fetchNotificationBadges,
  NOTIFICATIONS_CHANGED_EVENT,
} from './utils/levelHistoryApi';
import { fetchTodayQuests } from './utils/questApi';
import { processQuestCompletionSideEffects, syncDailyQuestHistoryIfNeeded } from './utils/questCompletionHandler';
import { QUEST_PROGRESS_CHANGED_EVENT } from './utils/questProgressReporter';
import { QUESTS_API_URL } from './config/questsApi';
import GamePrepNav from './components/GamePrepNav';
import MenuCaptureNav from './components/MenuCaptureNav';
import TrainingSessionNav from './components/TrainingSessionNav';
import TrainingModeNav from './components/TrainingModeNav';
import TrainingShortcutsLegend from './components/TrainingShortcutsLegend';
import GameDifficultyFooter from './components/GameDifficultyFooter';
import MasteryProgressView from './components/MasteryProgressView';
import useAppFitScale from './hooks/useAppFitScale';
import { TEXT_SCALE_FRAME_GROW, TEXT_SCALE_FRAME_GROW_X, useAccountTextScale } from './constants/textScalePreference';
import QuestView from './components/QuestView';
import QuestTreasureHallView from './components/QuestTreasureHallView';
import RadiantDayTitlesView from './components/RadiantDayTitlesView';
import RadiantDayBlasonsView from './components/RadiantDayBlasonsView';
import MenuQuestNav from './components/MenuQuestNav';
import SuccessEwssFilterModal, {
  SUCCESS_EWSS_FILTERS,
} from './components/SuccessEwssFilterModal';
import RegisterFilterModal from './components/RegisterFilterModal';
import RegisterSettingsModal from './components/RegisterSettingsModal';
import NotesSettingsModal from './components/NotesSettingsModal';
import NotesCategoryFilterDropdown from './components/NotesCategoryFilterDropdown';
import CaptureSettingsModal from './components/CaptureSettingsModal';
import QuestSettingsModal from './components/QuestSettingsModal';
import { GameSessionProvider } from './contexts/GameSessionContext';
import {
  DEFAULT_REGISTER_FILTERS,
  isRegisterFilterActive,
} from './constants/registerFilters';
import {
  DEFAULT_REGISTER_RECENCY_PREFERENCES,
  readRegisterPreferences,
  readRegisterRecencyPreferences,
  sanitizeRegisterRecencyPreferences,
  writeRegisterPreferences,
  writeRegisterRecencyPreferences,
} from './constants/registerPreferences';
import {
  DEFAULT_GROUPINGS_PREFERENCES,
  GROUPINGS_SEARCH_MODE_HELP,
  readGroupingsPreferences,
  sanitizeGroupingsPreferences,
  writeGroupingsPreferences,
} from './constants/groupingsPreferences';
import { matchesRegisterSearch, normalizeRegisterSearch } from './utils/registerWordListDisplay';
import {
  GROUPINGS_SEARCH_SCOPE_OPTIONS,
  GROUPINGS_SEARCH_SCOPES,
  getGroupingsScrollStorageKey,
  groupingIdsMatchingWordQuery,
  readGroupingsSearchScope,
  readGroupingsSelection,
  resolveGroupingIdFromWordQuery,
  writeGroupingsSearchScope,
  writeGroupingsSelection,
} from './utils/groupingsListPreference';
import {
  readNotesPreferences,
  writeNotesPreferences,
} from './constants/notesPreferences';
import {
  REGISTER_WORKSPACE_MODES,
  readRegisterWorkspaceMode,
  writeRegisterWorkspaceMode,
} from './utils/registerWorkspacePreference';
import {
  ACCOUNT_MENU_MODES,
  readAccountMenuMode,
  writeAccountMenuMode,
} from './utils/accountMenuPreference';
import {
  readCollectionTab,
  writeCollectionTab,
} from './utils/collectionTabPreference';
import {
  TRAINING_MODE_FREE,
  TRAINING_MODE_QUEST,
} from './constants/entrainementSetup';
import {
  readCapturePreferences,
  writeCapturePreferences,
} from './constants/capturePreferences';
import {
  readQuestPreferences,
  writeQuestPreferences,
} from './constants/questPreferences';
import {
  readQuestJournalSearchQuery,
  readQuestJournalSearchScope,
  writeQuestJournalSearchQuery,
  writeQuestJournalSearchScope,
} from './utils/questJournalPreference';
import {
  NOTES_SEARCH_SCOPES,
  readNotesSearchQuery,
  readNotesSearchScope,
  writeNotesSearchQuery,
  writeNotesSearchScope,
} from './utils/notesListPreference';
import { useAuth } from './components/AuthGate';
import KingdomMessaging from './components/KingdomMessaging';
import KingdomCitizens from './components/KingdomCitizens';
import LibraryLauncher from './components/LibraryLauncher';
import LibraryProgress, { LibraryFontSize } from './components/LibraryProgress';
import LibraryReadingTimer from './components/LibraryReadingTimer';
import LibraryView from './components/LibraryView';
import LibraryImportNotice from './components/LibraryImportNotice';
import { LIBRARY_FONT_DEFAULT, LIBRARY_FONT_STEPS, readLibraryPlace, stepLibraryFont } from './utils/libraryReading';
import { uploadLibraryBook } from './utils/libraryApi';
import PlayerMfaPanel from './components/PlayerMfaPanel';
import { warmItemImages } from './utils/itemImageWarmup';

const FILTER_SELECTION_LS_KEY = 'vocabularium_register_selection';
const ACTIVE_ACTION_LS_KEY = 'vocabularium_register_action';
const MENU_MODE_LS_KEY = 'vocabularium_menu_mode';
const QUEST_SCREEN_LS_KEY = 'vocabularium_quest_screen';
const STATS_SCREEN_LS_KEY = 'vocabularium_stats_screen';
const TRAINING_MODE_LS_KEY = 'vocabularium_training_mode';

const VALID_MENU_MODES = new Set([
  'register',
  'groupings',
  'capture',
  'training',
  'quest',
  'game',
  'collection',
  'medals',
  'stats',
  'cpanel',
  'library',
]);

function sortGroupements(items = []) {
  return [...items].sort((a, b) =>
    String(a.title || "").localeCompare(String(b.title || ""), "fr", {
      sensitivity: "base",
    })
  );
}

// Première arrivée (rien en mémoire) : le registre s'ouvre sur ce mot, en édition,
// pour que l'illustration soit visible d'emblée.
const FIRST_VISIT_WORD = 'espalier';
const FIRST_VISIT_ACTION = 'edit';

function readActiveAction() {
  try {
    const raw = localStorage.getItem(ACTIVE_ACTION_LS_KEY);
    if (raw === null) return FIRST_VISIT_ACTION;
    // "lastGameSummary" et "filteredStats" dépendent du filtre et ne sont pas persistés.
    return raw === 'edit' || raw === 'stats' || raw === 'recency' ? raw : 'stats';
  } catch {
    return 'stats';
  }
}

function writeActiveAction(action) {
  if (action !== 'stats' && action !== 'edit' && action !== 'recency') return;
  try {
    localStorage.setItem(ACTIVE_ACTION_LS_KEY, action);
  } catch {
    // quota / mode privé : on ignore
  }
}

function readFilterSelection() {
  try {
    const raw = localStorage.getItem(FILTER_SELECTION_LS_KEY);
    if (raw === null) return { info: FIRST_VISIT_WORD };
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeFilterSelection(map) {
  try {
    localStorage.setItem(FILTER_SELECTION_LS_KEY, JSON.stringify(map));
  } catch {
    // quota / mode privé : on ignore
  }
}

function readMenuMode() {
  try {
    const raw = localStorage.getItem(MENU_MODE_LS_KEY);
    if (raw === 'packs') return 'register';
    return VALID_MENU_MODES.has(raw) ? raw : 'register';
  } catch {
    return 'register';
  }
}

function writeMenuMode(mode) {
  if (!VALID_MENU_MODES.has(mode)) return;
  try {
    localStorage.setItem(MENU_MODE_LS_KEY, mode);
  } catch {
    // quota / mode privé : on ignore
  }
}

function readQuestScreen() {
  try {
    const raw = localStorage.getItem(QUEST_SCREEN_LS_KEY);
    return raw === 'treasures' || raw === 'daily' || raw === 'titles' || raw === 'blasons' ? raw : 'daily';
  } catch {
    return 'daily';
  }
}

function writeQuestScreen(screen) {
  if (screen !== 'daily' && screen !== 'treasures' && screen !== 'titles' && screen !== 'blasons') return;
  try {
    localStorage.setItem(QUEST_SCREEN_LS_KEY, screen);
  } catch {
    // quota / mode privé : on ignore
  }
}

function readStatsScreen() {
  try {
    const raw = localStorage.getItem(STATS_SCREEN_LS_KEY);
    return raw === 'table' || raw === 'charts' || raw === 'indicators' || raw === 'matrix'
      ? raw
      : 'table';
  } catch {
    return 'table';
  }
}

function writeStatsScreen(screen) {
  if (
    screen !== 'table' &&
    screen !== 'charts' &&
    screen !== 'indicators' &&
    screen !== 'matrix'
  ) {
    return;
  }
  try {
    localStorage.setItem(STATS_SCREEN_LS_KEY, screen);
  } catch {
    // quota / mode privé : on ignore
  }
}

function readTrainingMode() {
  try {
    const raw = localStorage.getItem(TRAINING_MODE_LS_KEY);
    return raw === TRAINING_MODE_QUEST ? TRAINING_MODE_QUEST : TRAINING_MODE_FREE;
  } catch {
    return TRAINING_MODE_FREE;
  }
}

function writeTrainingMode(nextMode) {
  try {
    localStorage.setItem(
      TRAINING_MODE_LS_KEY,
      nextMode === TRAINING_MODE_QUEST ? TRAINING_MODE_QUEST : TRAINING_MODE_FREE
    );
  } catch {
    // quota / mode privé : on ignore
  }
}

const LIBRARY_SHELF_HEAD_WIDTH = 7 * 152 + 6 * 10;
const LIBRARY_SHELF_PAD_X = 52;

function libraryFrameScale(frame) {
  const width = frame.getBoundingClientRect().width;
  const base = frame.offsetWidth;
  return base > 0 && width > 0 ? width / base : 1;
}

function libraryShelfEdges(frame) {
  const shelfSwitch = frame.querySelector(".library-mode-switch");
  const filter = frame.querySelector(".library-status-filter");
  const switchRect = shelfSwitch?.getBoundingClientRect();
  const filterRect = filter?.getBoundingClientRect();
  if (switchRect?.width && filterRect?.width) {
    return { left: switchRect.left, right: filterRect.right };
  }

  const scale = libraryFrameScale(frame);
  const frameRect = frame.getBoundingClientRect();
  const origin = frameRect.left + frame.clientLeft * scale;
  const inner = frame.clientWidth * 0.95 - LIBRARY_SHELF_PAD_X * 2;
  const headWidth = Math.min(LIBRARY_SHELF_HEAD_WIDTH, Math.max(0, inner));
  const headLeft = LIBRARY_SHELF_PAD_X + Math.max(0, (inner - headWidth) / 2);
  const left = origin + headLeft * scale;
  return { left, right: left + headWidth * scale };
}

function alignLibraryShelfControls(frame) {
  if (!frame) return;
  const scale = libraryFrameScale(frame);
  const edges = libraryShelfEdges(frame);
  const scope = frame.querySelector(".voletGauche .quest-journal-search-scope");
  const scopeParent = scope?.offsetParent;
  if (scope && scopeParent) {
    const parentLeft = scopeParent.getBoundingClientRect().left + scopeParent.clientLeft * scale;
    scope.style.removeProperty("left");
    frame.style.setProperty("--library-scope-left", `${(edges.left - parentLeft) / scale}px`);
  }

  const closeTip = frame.querySelector(".library-menu-close-tip");
  const closeParent = closeTip?.offsetParent;
  if (closeTip && closeParent) {
    const parentRect = closeParent.getBoundingClientRect();
    const borderRight = closeParent.offsetWidth - closeParent.clientWidth - closeParent.clientLeft;
    const paddingRight = parentRect.right - borderRight * scale;
    closeTip.style.removeProperty("right");
    frame.style.setProperty("--library-close-right", `${(paddingRight - edges.right) / scale}px`);
  }
}

function clearLibraryControlStyles(frame) {
  if (!frame) return;
  frame.style.removeProperty("--library-scope-left");
  frame.style.removeProperty("--library-close-right");
  frame.querySelectorAll(".menu-icon-tooltip, .quest-journal-search-scope").forEach((el) => {
    el.style.removeProperty("left");
    el.style.removeProperty("right");
  });
}

function App() {
  const { user } = useAuth();
  const [textScale] = useAccountTextScale(user?.sub || "");
  const questPlayerName = user?.displayName || "";
  const appFit = useAppFitScale(
    textScale > 1 ? TEXT_SCALE_FRAME_GROW : 0,
    textScale > 1 ? TEXT_SCALE_FRAME_GROW_X : 0
  );
  const [deepLinkedWord] = useState(() => readDeepLinkedWord());
  const [captureLink] = useState(() => readCaptureLink());
  // mode peut être: 'register' | 'capture' | ...
  const [mode, setMode] = useState(() => {
    if (captureLink) return 'capture';
    return deepLinkedWord ? 'register' : readMenuMode();
  });
  const [libraryQuery, setLibraryQuery] = useState("");
  const [librarySearchScope, setLibrarySearchScope] = useState("title");
  const [libraryHighlightQuery, setLibraryHighlightQuery] = useState("");
  const [libraryHighlightKind, setLibraryHighlightKind] = useState(null);
  const [libraryBookId, setLibraryBookId] = useState(null);
  const [libraryFont, setLibraryFont] = useState(LIBRARY_FONT_DEFAULT);
  const [libraryProgress, setLibraryProgress] = useState({ page: 1, total: 1, ready: false, shown: false });
  const [librarySeek, setLibrarySeek] = useState(null);
  const [libraryReload, setLibraryReload] = useState(0);
  const [libraryImportNotice, setLibraryImportNotice] = useState(null);
  const [libraryFavoritesOnly, setLibraryFavoritesOnly] = useState(false);
  const [libraryMarkTool, setLibraryMarkTool] = useState(null);
  const [libraryCharactersToken, setLibraryCharactersToken] = useState(0);
  const [librarySearchToken, setLibrarySearchToken] = useState(0);
  const [libraryHighlightsOpen, setLibraryHighlightsOpen] = useState(false);
  const [libraryResume, setLibraryResume] = useState(null);
  const [libraryResumeBook, setLibraryResumeBook] = useState(null);
  const [selectedWord, setSelectedWord] = useState(() => deepLinkedWord);
  const [selectedSide, setSelectedSide] = useState(() => (deepLinkedWord ? 'left' : null));
  const [activeFilter, setActiveFilter] = useState('info');
  const [wordsList, setWordsList] = useState([]);
  const [registerFilteredEntries, setRegisterFilteredEntries] = useState([]);
  const [activeAction, setActiveAction] = useState(() =>
    deepLinkedWord ? 'stats' : readActiveAction()
  );
  const [recencyStratum, setRecencyStratum] = useState(() => readRegisterRecencyStratum());
  const [searchQuery, setSearchQuery] = useState('');
  const [questJournalSearchQuery, setQuestJournalSearchQuery] = useState(() =>
    readQuestJournalSearchQuery()
  );
  const [questJournalSearchScope, setQuestJournalSearchScope] = useState(() =>
    readQuestJournalSearchScope()
  );
  const [notesSearchQuery, setNotesSearchQuery] = useState(() => readNotesSearchQuery());
  const [notesSearchScope, setNotesSearchScope] = useState(() => readNotesSearchScope());
  const [groupingsSearchQuery, setGroupingsSearchQuery] = useState('');
  const [groupingsSearchScope, setGroupingsSearchScope] = useState(() =>
    readGroupingsSearchScope()
  );
  const [groupings, setGroupings] = useState([]);
  const [groupementAffiliations, setGroupementAffiliations] = useState([]);
  const [selectedGroupingId, setSelectedGroupingId] = useState(() =>
    readGroupingsSelection()
  );
  const [selectedGroupingWords, setSelectedGroupingWords] = useState([]);
  const [groupingsPanelMode, setGroupingsPanelMode] = useState('idle');
  const [groupingsSuccessTitle, setGroupingsSuccessTitle] = useState('');
  const [groupingsCreateKey, setGroupingsCreateKey] = useState(0);
  const [groupingsPreferences, setGroupingsPreferences] = useState(() =>
    readGroupingsPreferences()
  );
  const [groupingsSettingsOpen, setGroupingsSettingsOpen] = useState(false);
  const [groupingsConfirm, setGroupingsConfirm] = useState(null);
  const [groupingsConfirmBusy, setGroupingsConfirmBusy] = useState(false);
  const [groupingsLoading, setGroupingsLoading] = useState(true);
  const [groupingWordsLoading, setGroupingWordsLoading] = useState(true);
  const groupingsSuccessTimerRef = useRef(null);
  const [listRefresh, setListRefresh] = useState(0);
  const [listCount, setListCount] = useState(0);
  const [listCountMeta, setListCountMeta] = useState(null);
  const [registerListLoading, setRegisterListLoading] = useState(false);
  const [successEwssFilter, setSuccessEwssFilter] = useState(SUCCESS_EWSS_FILTERS.ALL);
  const [successFilterModalOpen, setSuccessFilterModalOpen] = useState(false);
  const [registerFilters, setRegisterFilters] = useState(DEFAULT_REGISTER_FILTERS);
  const [registerFilterModalOpen, setRegisterFilterModalOpen] = useState(false);
  const [registerSettingsModalOpen, setRegisterSettingsModalOpen] = useState(false);
  const [registerPreferences, setRegisterPreferences] = useState(() => readRegisterPreferences());
  const [recencySettingsModalOpen, setRecencySettingsModalOpen] = useState(false);
  const [recencyPreferences, setRecencyPreferences] = useState(() =>
    readRegisterRecencyPreferences()
  );
  const [registerWorkspaceMode, setRegisterWorkspaceMode] = useState(() =>
    deepLinkedWord
      ? REGISTER_WORKSPACE_MODES.REGISTER
      : readRegisterWorkspaceMode()
  );
  const [accountMenuMode, setAccountMenuMode] = useState(() => readAccountMenuMode());
  const [notesPreferences, setNotesPreferences] = useState(() => readNotesPreferences());
  const [notesSettingsModalOpen, setNotesSettingsModalOpen] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesHasNote, setNotesHasNote] = useState(false);
  const [notesSpread, setNotesSpread] = useState({
    open: false,
    page: 1,
    total: 1,
    fontSize: LIBRARY_FONT_DEFAULT,
    typeface: "georgia",
  });
  const notesControllerRef = useRef(null);
  const [collectionTab, setCollectionTab] = useState(() => readCollectionTab());
  const [masteryScreen, setMasteryScreen] = useState('progress');
  const [notificationBadges, setNotificationBadges] = useState({
    medals: 0,
    packs: 0,
    packsAlert: false,
    history: 0,
    cloverRewards: 0,
    quests: 0,
  });
  const [statsScreen, setStatsScreen] = useState(() => readStatsScreen());
  const [cpanelScreen, setCpanelScreen] = useState('requests');
  const [questScreen, setQuestScreen] = useState(() => readQuestScreen());
  const [questTreasureFocus, setQuestTreasureFocus] = useState(null);
  const [questCountdown, setQuestCountdown] = useState(null);
  const [questSettingsModalOpen, setQuestSettingsModalOpen] = useState(false);
  const [questPreferences, setQuestPreferences] = useState(() => readQuestPreferences());
  const [capturePanel, setCapturePanel] = useState('capture');
  const [captureDraft, setCaptureDraft] = useState(() =>
    captureLink ? { ...captureLink, token: Date.now() } : null
  );
  const [captureSettingsModalOpen, setCaptureSettingsModalOpen] = useState(false);
  const [capturePreferences, setCapturePreferences] = useState(() => readCapturePreferences());
  const [trainingPhase, setTrainingPhase] = useState('setup');
  const [trainingMode, setTrainingMode] = useState(() => readTrainingMode());
  const [questTrainingAvailable, setQuestTrainingAvailable] = useState(false);
  const trainingStopSessionRef = useRef(null);
  const filterSelectionRef = useRef(null);
  if (filterSelectionRef.current === null) {
    const map = readFilterSelection();
    if (deepLinkedWord) {
      map.info = deepLinkedWord;
      writeFilterSelection(map);
    }
    filterSelectionRef.current = map;
  }
  const detailScrollRef = useRef(null);
  const detailContentRef = useRef(null);
  const [detailScrollFades, setDetailScrollFades] = useState({ top: false, bottom: false });
  // Positionnement automatique du volet détail (préférence de scroll).
  const anchorSessionRef = useRef(false); // ancrage en cours (mot/onglet/préf changé)
  const anchorRafRef = useRef(0);
  const anchorStartRef = useRef(0);
  const anchorStableRef = useRef(0); // nb de frames sans ajustement (contenu stabilisé)
  const lastAnchoredTopRef = useRef(null); // dernière position posée par nous

  const updateDetailScrollFades = useCallback(() => {
    const el = detailScrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setDetailScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  const getActiveScrollAnchor = useCallback(() => {
    if (activeAction === "stats") return registerPreferences.statsScrollAnchor;
    if (activeAction === "edit") return registerPreferences.editScrollAnchor;
    return "free";
  }, [activeAction, registerPreferences.statsScrollAnchor, registerPreferences.editScrollAnchor]);

  // Applique une passe d'ancrage. Renvoie true si la session doit se terminer
  // (mode libre, sélecteur inconnu, ou contenu positionné et stabilisé).
  const applyScrollAnchorPass = useCallback(() => {
    const container = detailScrollRef.current;
    const content = detailContentRef.current;
    if (!container || !content) return false;

    const anchor = getActiveScrollAnchor();
    if (!anchor || anchor === "free") return true;

    const selector = {
      statsTable: '[data-scroll-anchor="stats-table"]',
      definition: '[data-scroll-anchor="definition"]',
      extrait: '[data-scroll-anchor="extrait"]',
    }[anchor];
    if (!selector) return true;

    const target = content.querySelector(selector);
    if (!target) {
      // Contenu pas encore chargé : on réinitialise le compteur de stabilité
      // et on laissera la boucle réessayer.
      anchorStableRef.current = 0;
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    // Aligne le haut de l'élément sur le haut de la zone (marge de 8px). Clampé
    // pour ne jamais dépasser le scroll possible ; si l'élément est plus grand
    // que la zone, il reste calé en haut, le reste étant défilable.
    const desired = Math.max(
      0,
      Math.min(container.scrollTop + (targetRect.top - containerRect.top) - 8, maxTop)
    );

    if (Math.abs(desired - container.scrollTop) > 1) {
      container.scrollTop = desired;
      anchorStableRef.current = 0;
    } else {
      anchorStableRef.current += 1;
    }
    lastAnchoredTopRef.current = container.scrollTop;
    // Positionné et stable sur quelques frames consécutives → on peut arrêter.
    return anchorStableRef.current >= 3;
  }, [getActiveScrollAnchor]);

  const handleDetailScroll = useCallback(() => {
    updateDetailScrollFades();
  }, [updateDetailScrollFades]);

  useEffect(() => {
    updateDetailScrollFades();
  }, [selectedWord, activeAction, listRefresh, updateDetailScrollFades]);

  // Pierres / potions / parchemins : téléchargés et décodés une fois, en tâche
  // de fond, pour que les cartes s'affichent d'un bloc au lieu d'en cascade.
  useEffect(() => {
    warmItemImages();
  }, []);

  // Nouveau mot / nouvel onglet / préférence : on ouvre une « session d'ancrage »
  // qui repositionne à chaque frame (le temps que le contenu asynchrone se charge
  // et se stabilise), et qui s'arrête dès que l'utilisateur scrolle lui-même
  // (détecté en comparant scrollTop à la dernière valeur que nous avons posée).
  useEffect(() => {
    const anchor = getActiveScrollAnchor();
    if (!anchor || anchor === "free") return undefined;

    anchorSessionRef.current = true;
    anchorStartRef.current =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    anchorStableRef.current = 0;
    lastAnchoredTopRef.current = null;
    cancelAnimationFrame(anchorRafRef.current);

    const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
    const MAX_DURATION_MS = 2500;

    const tick = () => {
      if (!anchorSessionRef.current) return;
      const container = detailScrollRef.current;

      // Détection d'un scroll utilisateur : scrollTop diffère de ce que nous
      // avons posé (la croissance du contenu, elle, ne change pas scrollTop).
      if (
        lastAnchoredTopRef.current != null &&
        container &&
        Math.abs(container.scrollTop - lastAnchoredTopRef.current) > 2
      ) {
        anchorSessionRef.current = false;
        return;
      }

      const done = applyScrollAnchorPass();
      if (done || now() - anchorStartRef.current > MAX_DURATION_MS) {
        anchorSessionRef.current = false;
        return;
      }
      anchorRafRef.current = requestAnimationFrame(tick);
    };

    anchorRafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(anchorRafRef.current);
      anchorSessionRef.current = false;
    };
  }, [selectedWord, activeAction, getActiveScrollAnchor, applyScrollAnchorPass]);

  useEffect(() => {
    writeRegisterPreferences(registerPreferences);
  }, [registerPreferences]);

  useEffect(() => {
    writeGroupingsPreferences(groupingsPreferences);
  }, [groupingsPreferences]);

  useEffect(() => {
    if (!deepLinkedWord) return;
    writeMenuMode('register');
    writeRegisterWorkspaceMode(REGISTER_WORKSPACE_MODES.REGISTER);
  }, [deepLinkedWord]);

  useEffect(() => {
    writeRegisterRecencyPreferences(recencyPreferences);
  }, [recencyPreferences]);

  useEffect(() => {
    writeNotesPreferences(notesPreferences);
  }, [notesPreferences]);

  useEffect(() => {
    writeQuestPreferences(questPreferences);
  }, [questPreferences]);

  const handleQuestPreferencesChange = useCallback((nextPreferences) => {
    setQuestPreferences(nextPreferences);
  }, []);

  useEffect(() => {
    writeCapturePreferences(capturePreferences);
  }, [capturePreferences]);

  useEffect(() => {
    if (captureLink && mode === "capture") return;
    writeMenuMode(mode);
  }, [mode, captureLink]);

  useEffect(() => {
    if (mode === 'cpanel' && !user?.admin) {
      setMode('register');
    }
  }, [mode, user?.admin]);

  useEffect(() => {
    if (mode === 'cpanel' && user?.admin) {
      setAccountMenuMode(writeAccountMenuMode(ACCOUNT_MENU_MODES.CPANEL));
    }
  }, [mode, user?.admin]);

  useEffect(() => {
    writeQuestScreen(questScreen);
  }, [questScreen]);

  useEffect(() => {
    writeStatsScreen(statsScreen);
  }, [statsScreen]);

  useEffect(() => {
    writeTrainingMode(trainingMode);
  }, [trainingMode]);

  const refreshNotificationBadges = useCallback(async () => {
    try {
      const badgesPromise = fetchNotificationBadges();
      const questsPromise = QUESTS_API_URL
        ? fetchTodayQuests().catch(() => null)
        : Promise.resolve(null);

      const [badges, quests] = await Promise.all([badgesPromise, questsPromise]);

      setNotificationBadges({
        medals: badges.medalsUnread,
        packs: badges.packsUnread,
        packsAlert: badges.packsAlert,
        history: badges.historyUnread,
        cloverRewards: badges.cloverRewardsUnread,
        quests: quests?.found ? quests.unreadCompletedCount || 0 : 0,
      });

      if (quests?.found) {
        await processQuestCompletionSideEffects(quests);
      }
    } catch (err) {
      console.error("Erreur chargement badges notifications :", err);
    }
  }, []);

  useEffect(() => {
    refreshNotificationBadges();
  }, [refreshNotificationBadges]);

  useEffect(() => {
    const handleQuestProgressChanged = () => {
      syncDailyQuestHistoryIfNeeded()
        .then((recorded) => {
          if (recorded) {
            refreshNotificationBadges();
          }
        })
        .catch(() => {});
    };

    window.addEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleQuestProgressChanged);
    return () => {
      window.removeEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleQuestProgressChanged);
    };
  }, [refreshNotificationBadges]);

  useEffect(() => {
    const handleNotificationsChanged = () => {
      refreshNotificationBadges();
    };

    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
    };
  }, [refreshNotificationBadges]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshNotificationBadges();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshNotificationBadges]);

  useEffect(() => {
    if (mode !== 'medals') return undefined;

    syncDailyQuestHistoryIfNeeded()
      .then((recorded) => {
        if (recorded) {
          refreshNotificationBadges();
        }
      })
      .catch((err) => {
        console.warn("Synchronisation historique quête :", err.message);
      });

    return undefined;
  }, [mode, refreshNotificationBadges]);

  useEffect(() => {
    const scrollEl = detailScrollRef.current;
    const contentEl = detailContentRef.current;
    if (!scrollEl && !contentEl) return;

    const observer = new ResizeObserver(updateDetailScrollFades);
    // Le conteneur a une hauteur fixe : c'est le contenu interne (chargé de façon
    // asynchrone) dont la hauteur change → on l'observe pour recalculer le fondu.
    if (scrollEl) observer.observe(scrollEl);
    if (contentEl) observer.observe(contentEl);
    return () => observer.disconnect();
  }, [updateDetailScrollFades, mode, selectedWord, activeAction]);

  const rememberFilterSelection = useCallback((filter, word) => {
    if (filter && word) {
      filterSelectionRef.current[filter] = word;
      writeFilterSelection(filterSelectionRef.current);
    }
  }, []);

  const handleSelectWord = (word, side, { userInitiated = false } = {}) => {
    setSelectedWord(word);
    setSelectedSide(side);
    rememberFilterSelection(activeFilter, word);
    if (
      userInitiated &&
      word &&
      (activeAction === "filteredStats" ||
        activeAction === "recency" ||
        (activeAction === "lastGameSummary" && activeFilter === "lastGame"))
    ) {
      setActiveAction("stats");
      writeActiveAction("stats");
    }
  };

  const handleFilterChange = (filterName) => {
    rememberFilterSelection(activeFilter, selectedWord);
    setActiveFilter(filterName);
    if (filterName !== "info") {
      setRegisterSettingsModalOpen(false);
    }
    if (filterName === "lastGame") {
      setActiveAction("lastGameSummary");
      writeActiveAction("stats");
    } else {
      setActiveAction("stats");
      writeActiveAction("stats");
    }
  };

  const handleGameContinueToMastery = () => {
    setMasteryScreen('progress');
    setMode('medals');
  };

  // Depuis l'historique des parties : reconstitue le pool de mots d'une partie
  // en filtrant le registre sur la date + l'heure de jeu de cette partie.
  const handleViewSessionPool = (session) => {
    if (!session) return;
    setRegisterFilters({
      ...DEFAULT_REGISTER_FILTERS,
      playDateStart: session.date || '',
      playDateEnd: session.date || '',
      playTimeStart: session.time || '',
      playTimeEnd: session.time || '',
    });
    setSearchQuery('');
    setSuccessFilterModalOpen(false);
    setRegisterFilterModalOpen(false);
    setRegisterSettingsModalOpen(false);
    setActiveFilter('info');
    setActiveAction('stats');
    writeActiveAction('stats');
    setSelectedWord(null);
    setSelectedSide(null);
    setMode('register');
    setListRefresh((n) => n + 1);
  };

  const handleHighlightKindChange = useCallback((kind) => {
    setLibraryHighlightKind((current) => {
      if (kind && current && current !== kind) setLibraryHighlightQuery("");
      return kind;
    });
  }, []);

  const handleLibraryProgress = useCallback((next) => {
    setLibraryProgress((current) =>
      current.page === next.page &&
      current.total === next.total &&
      current.ready === next.ready &&
      current.shown === next.shown
        ? current
        : next
    );
  }, []);

  const handleLibrarySeekApplied = useCallback(() => {
    setLibrarySeek(null);
  }, []);

  const libraryScreenRef = useRef({ highlightsOpen: false, kind: null });
  const libraryReturnRef = useRef(null);
  libraryScreenRef.current = {
    highlightsOpen: libraryHighlightsOpen,
    kind: libraryHighlightKind,
  };

  const handleOpenLibraryBook = useCallback((id, origin) => {
    const screen = libraryScreenRef.current;
    libraryReturnRef.current =
      screen.highlightsOpen && screen.kind
        ? {
            bookId: id,
            kind: screen.kind,
            scrollTop: Math.max(0, Math.round(Number(origin?.scrollTop) || 0)),
          }
        : null;
    const place = readLibraryPlace(id);
    setLibraryFont(place?.fontSize || LIBRARY_FONT_DEFAULT);
    setLibraryProgress({ page: 1, total: 1, ready: false, shown: false });
    setLibrarySeek(null);
    setLibraryHighlightsOpen(false);
    setLibraryBookId(id);
  }, []);

  const handleLibraryHome = useCallback(() => {
    const back = libraryReturnRef.current;
    libraryReturnRef.current = null;
    setLibraryBookId(null);
    setLibraryMarkTool(null);
    if (!back) return;
    setLibraryHighlightKind(back.kind);
    setLibraryResume(back);
    setLibraryHighlightsOpen(true);
  }, []);

  const handleHighlightResumeApplied = useCallback(() => {
    setLibraryResume(null);
  }, []);

  useEffect(() => {
    setLibraryMarkTool(libraryBookId ? "passage" : null);
  }, [libraryBookId]);

  useLayoutEffect(() => {
    const frame = document.querySelector(".custom-pattern");
    if (!frame) return undefined;
    const notesSpreadChrome =
      mode === "register" &&
      registerWorkspaceMode === REGISTER_WORKSPACE_MODES.NOTES &&
      notesSpread.open;
    if (mode !== "library" && !notesSpreadChrome) {
      clearLibraryControlStyles(frame);
      return undefined;
    }
    const align = () => alignLibraryShelfControls(frame);
    align();
    const observer = new ResizeObserver(align);
    observer.observe(frame);
    const content = frame.querySelector(".vdroite-content");
    if (content) observer.observe(content);
    return () => {
      observer.disconnect();
      clearLibraryControlStyles(frame);
    };
  }, [
    mode,
    libraryBookId,
    libraryHighlightsOpen,
    textScale,
    appFit.frameWidth,
    appFit.scale,
    registerWorkspaceMode,
    notesSpread.open,
  ]);

  const handleLibraryImport = useCallback(async (file) => {
    const rawName = String(file?.name || "").trim();
    const fallback = rawName.replace(/\.epub$/i, "").trim() || rawName || "Ce livre";
    if (!rawName.toLowerCase().endsWith(".epub")) {
      setLibraryImportNotice({
        ok: false,
        title: fallback,
        detail: "Choisissez un fichier EPUB.",
      });
      return;
    }
    setLibraryImportNotice(null);
    try {
      const book = await uploadLibraryBook(file);
      setLibraryReload((n) => n + 1);
      const title = String(book?.title || "").trim() || fallback;
      setLibraryImportNotice({ ok: true, title });
    } catch (err) {
      setLibraryImportNotice({
        ok: false,
        duplicate: Boolean(err?.duplicate),
        title: fallback,
        detail: err?.duplicate ? "" : String(err?.message || "").trim(),
      });
    }
  }, []);

  const handleLibraryImportNoticeClose = useCallback(() => {
    setLibraryImportNotice(null);
  }, []);

  const commitIconModeChangeRef = useRef(null);
  commitIconModeChangeRef.current = (newMode) => {
    setMode(newMode);
    if (newMode !== 'register') {
      setNotesSettingsModalOpen(false);
    } else if (activeAction === 'recency') {
      setActiveAction('stats');
      writeActiveAction('stats');
    }

    if (newMode !== 'training') {
      setTrainingPhase('setup');
    }

    if (newMode !== 'library') {
      setLibraryBookId(null);
    }

    if (newMode === 'capture') {
      setCapturePanel('capture');
    } else {
      setCaptureDraft(null);
    }

    if (newMode === 'register') {
      if (registerWorkspaceMode === REGISTER_WORKSPACE_MODES.REGISTER) {
        setListRefresh((n) => n + 1);
        const saved = filterSelectionRef.current[activeFilter];
        if (saved) {
          setSelectedWord(saved);
          setSelectedSide('left');
        } else if (wordsList.length > 0) {
          setSelectedWord(wordsList[0]);
          setSelectedSide('left');
        } else {
    setSelectedWord(null);
    setSelectedSide(null);
        }
      } else {
        setSelectedWord(null);
        setSelectedSide(null);
      }
    } else {
      setSelectedWord(null);
      setSelectedSide(null);
    }
  };

  const handleIconModeChange = (newMode) => {
    if (mode === "training" && newMode !== "training" && trainingPhase === "session") {
      const leave = trainingStopSessionRef.current;
      if (
        typeof leave === "function" &&
        leave(() => commitIconModeChangeRef.current?.(newMode))
      ) {
        return;
      }
    }
    commitIconModeChangeRef.current?.(newMode);
  };

  const handleCaptureHighlightedWord = (draft) => {
    setCaptureDraft({
      mot: draft?.mot || "",
      livre: draft?.livre || "",
      auteur: draft?.auteur || "",
      extrait: draft?.extrait || "",
      token: Date.now(),
    });
    handleIconModeChange("capture");
  };

  const handleRegisterWorkspaceChange = (nextMode) => {
    const sanitized = writeRegisterWorkspaceMode(nextMode);
    setRegisterWorkspaceMode(sanitized);
    setRegisterFilterModalOpen(false);
    setRegisterSettingsModalOpen(false);

    if (sanitized === REGISTER_WORKSPACE_MODES.REGISTER && mode === 'register') {
      setListRefresh((n) => n + 1);
      const saved = filterSelectionRef.current[activeFilter];
      if (saved) {
        setSelectedWord(saved);
        setSelectedSide('left');
      } else if (wordsList.length > 0) {
        setSelectedWord(wordsList[0]);
        setSelectedSide('left');
      }
    }

    if (sanitized === REGISTER_WORKSPACE_MODES.NOTES) {
      setSelectedWord(null);
      setSelectedSide(null);
      setNotesDirty(false);
    } else {
      setNotesSettingsModalOpen(false);
      setNotesDirty(false);
    }
  };

  const handleAccountMenuWorkspaceChange = (nextMode) => {
    const sanitized = writeAccountMenuMode(nextMode);
    setAccountMenuMode(sanitized);
    if (sanitized === ACCOUNT_MENU_MODES.CPANEL) {
      setMode('cpanel');
    } else if (mode === 'cpanel') {
      setMode('register');
    }
  };

  const handleNotesMetaChange = useCallback((meta) => {
    setNotesDirty(Boolean(meta.dirty));
    setNotesEditing(Boolean(meta.isEditing));
    setNotesHasNote(Boolean(meta.hasNote));
    setNotesSpread((current) => {
      const next = {
        open: Boolean(meta.spreadOpen),
        page: meta.spreadPage || 1,
        total: meta.spreadTotal || 1,
        fontSize: meta.spreadFont || LIBRARY_FONT_DEFAULT,
        typeface: meta.spreadTypeface === "comic" ? "comic" : "georgia",
      };
      if (
        current.open === next.open &&
        current.page === next.page &&
        current.total === next.total &&
        current.fontSize === next.fontSize &&
        current.typeface === next.typeface
      ) {
        return current;
      }
      return next;
    });
  }, []);

  const handleNotesSave = useCallback(() => {
    notesControllerRef.current?.save?.();
  }, []);

  const handleNotesRead = useCallback(() => {
    notesControllerRef.current?.read?.();
  }, []);

  const handleNotesEdit = useCallback(() => {
    notesControllerRef.current?.edit?.();
  }, []);

  const handleNotesDelete = useCallback(() => {
    notesControllerRef.current?.delete?.();
  }, []);

  const handleNotesCreate = useCallback(() => {
    if (notesEditing) return;
    notesControllerRef.current?.create?.();
  }, [notesEditing]);

  const handleNotesPreferencesChange = useCallback((nextPreferences) => {
    setNotesPreferences(nextPreferences);
  }, []);

  const handleNotesCategoryFilterChange = useCallback((categoryFilter) => {
    setNotesPreferences((prev) => ({ ...prev, categoryFilter }));
  }, []);

  const handleCollectionTabChange = (tab) => {
    setCollectionTab(tab);
    writeCollectionTab(tab);
  };

  const handleActionChange = (action) => {
  setActiveAction(action);
    writeActiveAction(action);
  };

  const handleRecencyStratumChange = (stratumId) => {
    setRecencyStratum(writeRegisterRecencyStratum(stratumId));
  };

  const handleListRefresh = () => {
    setListRefresh((n) => n + 1);
  };

  const handleWordRenamed = (oldMot, newMot) => {
    if (!oldMot || !newMot || oldMot === newMot) return;

    setSelectedWord(newMot);
    setWordsList((list) =>
      Array.isArray(list) ? list.map((entry) => (entry === oldMot ? newMot : entry)) : list
    );

    const map = { ...(filterSelectionRef.current || {}) };
    let changed = false;
    for (const key of Object.keys(map)) {
      if (map[key] === oldMot) {
        map[key] = newMot;
        changed = true;
      }
    }
    if (activeFilter && map[activeFilter] !== newMot) {
      map[activeFilter] = newMot;
      changed = true;
    }
    if (changed) {
      filterSelectionRef.current = map;
      writeFilterSelection(map);
    }

    handleListRefresh();
  };

  const handleWordsLoaded = (list) => {
  setWordsList(list);
  };

  const handleFilteredEntriesChange = useCallback((entries) => {
    setRegisterFilteredEntries(Array.isArray(entries) ? entries : []);
  }, []);

  const handleRegisterListLoadingChange = useCallback((isLoading) => {
    setRegisterListLoading(Boolean(isLoading));
  }, []);

  const handleCountChange = (payload) => {
    if (typeof payload === "number") {
      setListCount(payload);
      setListCountMeta(null);
      return;
    }
    setListCount(payload.count ?? 0);
    setListCountMeta(payload);
  };

  const formatPercent = (value) => {
    if (value == null || Number.isNaN(value)) return "0,0";
    return Number(value).toFixed(1).replace(".", ",");
  };

  const registerTrainingStopSession = useCallback((handler) => {
    trainingStopSessionRef.current = handler;
  }, []);

  const handleTrainingConfigClick = useCallback(() => {
    trainingStopSessionRef.current?.();
  }, []);

  const handleQuestJournalSearchChange = useCallback((query) => {
    setQuestJournalSearchQuery(query);
    writeQuestJournalSearchQuery(query);
  }, []);

  const handleQuestJournalSearchScopeChange = useCallback((scope) => {
    setQuestJournalSearchScope(scope);
    writeQuestJournalSearchScope(scope);
  }, []);

  const handleNotesSearchChange = useCallback((query) => {
    setNotesSearchQuery(query);
    writeNotesSearchQuery(query);
  }, []);

  const handleNotesSearchScopeChange = useCallback((scope) => {
    setNotesSearchScope(scope);
    writeNotesSearchScope(scope);
  }, []);

  const handleGroupingsSearchScopeChange = useCallback((scope) => {
    setGroupingsSearchScope(scope);
    writeGroupingsSearchScope(scope);
  }, []);

  const leftPanelHidden =
    mode === 'game' ||
    mode === 'capture' ||
    mode === 'training' ||
    (mode === 'quest' && questScreen !== 'daily') ||
    mode === 'stats' ||
    mode === 'cpanel' ||
    mode === 'collection' ||
    mode === 'medals';

  const trainingSessionActive = mode === 'training' && trainingPhase === 'session';
  const trainingSetupActive = mode === 'training' && trainingPhase === 'setup';
  const isRegisterWordsWorkspace =
    mode === 'register' && registerWorkspaceMode === REGISTER_WORKSPACE_MODES.REGISTER;
  const isRegisterNotesWorkspace =
    mode === 'register' && registerWorkspaceMode === REGISTER_WORKSPACE_MODES.NOTES;
  const isGroupingsWorkspace = mode === 'groupings';
  const isRegisterRecencyScreen =
    isRegisterWordsWorkspace && activeAction === 'recency';
  const selectedGrouping =
    groupings.find((grouping) => grouping.groupementId === selectedGroupingId) || null;
  const visibleGroupings = groupings.filter((grouping) => {
    const query = normalizeRegisterSearch(groupingsSearchQuery);
    if (!query) return true;
    if (groupingsSearchScope === GROUPINGS_SEARCH_SCOPES.WORD) {
      const matchingIds = groupingIdsMatchingWordQuery(
        groupementAffiliations,
        query,
        groupingsPreferences.searchMode
      );
      return matchingIds ? matchingIds.has(grouping.groupementId) : true;
    }
    return matchesRegisterSearch(
      { mot: grouping.title },
      query,
      groupingsPreferences.searchMode
    );
  });
  const visibleGroupingsWordCount = visibleGroupings.reduce(
    (total, grouping) => total + (Number(grouping.wordCount) || 0),
    0
  );
  const usedGroupementColors = groupings.map((grouping) => grouping.color);
  const showGroupingsListSkeleton = groupingsLoading;
  const showGroupingsDetailSkeleton = groupingsLoading || groupingWordsLoading;

  const clearGroupingsSuccessTimer = useCallback(() => {
    if (groupingsSuccessTimerRef.current) {
      clearTimeout(groupingsSuccessTimerRef.current);
      groupingsSuccessTimerRef.current = null;
    }
  }, []);

  const loadGroupements = useCallback(async () => {
    const { items, affiliations } = await fetchGroupementsCatalog();
    setGroupings(sortGroupements(items));
    setGroupementAffiliations(affiliations);
    return items;
  }, []);

  const loadGroupementWords = useCallback(async (groupementId) => {
    if (!groupementId) {
      setSelectedGroupingWords([]);
      return [];
    }
    const mots = await fetchGroupementMots(groupementId);
    setSelectedGroupingWords(mots);
    return mots;
  }, []);

  const handleSelectGrouping = useCallback((groupementId) => {
    clearGroupingsSuccessTimer();
    setSelectedGroupingId(groupementId);
    writeGroupingsSelection(groupementId);
    setGroupingsPanelMode("detail");
    setGroupingsSuccessTitle("");
    loadGroupementWords(groupementId).catch(() => setSelectedGroupingWords([]));
  }, [clearGroupingsSuccessTimer, loadGroupementWords]);

  const handleStartGroupingCreate = useCallback(() => {
    clearGroupingsSuccessTimer();
    setGroupingsCreateKey((value) => value + 1);
    setGroupingsPanelMode("create");
    setGroupingsSuccessTitle("");
  }, [clearGroupingsSuccessTimer]);

  const handleCancelGroupingCreate = useCallback(() => {
    setGroupingsPanelMode(selectedGroupingId ? "detail" : "idle");
  }, [selectedGroupingId]);

  const handleGroupingCreated = useCallback(async ({ title, color }) => {
    const created = await createGroupement({ title, color });
    await loadGroupements();
    setSelectedGroupingId(created.groupementId);
    writeGroupingsSelection(created.groupementId);
    setSelectedGroupingWords([]);
    setGroupingsSuccessTitle(created.title);
    setGroupingsPanelMode("success");
    clearGroupingsSuccessTimer();
    groupingsSuccessTimerRef.current = setTimeout(() => {
      setGroupingsPanelMode("detail");
      setGroupingsSuccessTitle("");
      groupingsSuccessTimerRef.current = null;
    }, 4000);
  }, [clearGroupingsSuccessTimer, loadGroupements]);

  const handleDeleteGrouping = useCallback(async () => {
    if (!selectedGrouping) return;
    setGroupingsConfirmBusy(true);
    try {
      await deleteGroupement(selectedGrouping.groupementId);
      clearGroupingsSuccessTimer();
      setGroupingsConfirm(null);
      const items = await loadGroupements();
      const next = items[0] || null;
      setSelectedGroupingId(next ? next.groupementId : null);
      writeGroupingsSelection(next ? next.groupementId : null);
      setGroupingsPanelMode(next ? "detail" : "idle");
      if (next) {
        await loadGroupementWords(next.groupementId);
      } else {
        setSelectedGroupingWords([]);
      }
    } catch (error) {
      alert(error.message || "Impossible de supprimer ce groupement.");
    } finally {
      setGroupingsConfirmBusy(false);
    }
  }, [
    clearGroupingsSuccessTimer,
    loadGroupementWords,
    loadGroupements,
    selectedGrouping,
  ]);

  const handleEmptyGrouping = useCallback(async () => {
    if (!selectedGrouping) return;
    setGroupingsConfirmBusy(true);
    try {
      await emptyGroupement(selectedGrouping.groupementId);
      setGroupingsConfirm(null);
      await loadGroupements();
      await loadGroupementWords(selectedGrouping.groupementId);
    } catch (error) {
      alert(error.message || "Impossible de vider ce groupement.");
    } finally {
      setGroupingsConfirmBusy(false);
    }
  }, [loadGroupementWords, loadGroupements, selectedGrouping]);

  const handleReassignWordsFromGrouping = useCallback(async (affectations) => {
    if (!selectedGrouping) return;
    setGroupingsConfirmBusy(true);
    try {
      await reassignWordsFromGroupement(selectedGrouping.groupementId, affectations);
      setGroupingsConfirm(null);
      await loadGroupements();
      await loadGroupementWords(selectedGrouping.groupementId);
    } catch (error) {
      alert(error.message || "Impossible de réaffecter ces mots.");
    } finally {
      setGroupingsConfirmBusy(false);
    }
  }, [loadGroupementWords, loadGroupements, selectedGrouping]);

  const handleRemoveWordsFromGrouping = useCallback(async (mots) => {
    if (!selectedGrouping) return;
    setGroupingsConfirmBusy(true);
    try {
      await removeWordsFromGroupement(selectedGrouping.groupementId, mots);
      setGroupingsConfirm(null);
      await loadGroupements();
      await loadGroupementWords(selectedGrouping.groupementId);
    } catch (error) {
      alert(error.message || "Impossible de retirer ces mots du groupement.");
    } finally {
      setGroupingsConfirmBusy(false);
    }
  }, [loadGroupementWords, loadGroupements, selectedGrouping]);

  const handleRenameGrouping = useCallback(
    async ({ title, color }) => {
      if (!selectedGrouping) return;
      await updateGroupement(selectedGrouping.groupementId, { title, color });
      await loadGroupements();
    },
    [loadGroupements, selectedGrouping]
  );

  useEffect(() => {
    if (mode !== "groupings") {
      clearGroupingsSuccessTimer();
      setGroupingsSettingsOpen(false);
      setGroupingsConfirm(null);
      setGroupingsSuccessTitle("");
      setGroupingsPanelMode((current) =>
        current === "success" ? "detail" : current
      );
      return undefined;
    }
    let cancelled = false;
    loadGroupements()
      .then((items) => {
        if (cancelled) return;
        if (!items.length) {
          setSelectedGroupingId(null);
          writeGroupingsSelection(null);
          setSelectedGroupingWords([]);
          setGroupingWordsLoading(false);
          setGroupingsPanelMode((current) =>
            current === "create" ? current : "idle"
          );
          return;
        }
        setSelectedGroupingId((current) => {
          if (current && items.some((item) => item.groupementId === current)) {
            return current;
          }
          const stored = readGroupingsSelection();
          if (stored && items.some((item) => item.groupementId === stored)) {
            return stored;
          }
          writeGroupingsSelection(items[0].groupementId);
          return items[0].groupementId;
        });
        setGroupingsPanelMode((current) =>
          current === "create" ? current : "detail"
        );
      })
      .catch(() => {
        if (!cancelled) {
          setGroupings([]);
          setGroupementAffiliations([]);
          setGroupingWordsLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) setGroupingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clearGroupingsSuccessTimer, loadGroupements, mode]);

  useEffect(() => {
    if (mode !== "groupings" || groupingsPanelMode !== "detail" || !selectedGroupingId) {
      return undefined;
    }
    let cancelled = false;
    loadGroupementWords(selectedGroupingId)
      .catch(() => {
        if (!cancelled) setSelectedGroupingWords([]);
      })
      .finally(() => {
        if (!cancelled) setGroupingWordsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupingsPanelMode, loadGroupementWords, mode, selectedGroupingId]);

  useEffect(() => {
    if (mode !== "groupings") {
      return undefined;
    }
    if (groupingsPanelMode === "create" || groupingsPanelMode === "success") {
      return undefined;
    }
    if (groupingsSearchScope !== GROUPINGS_SEARCH_SCOPES.WORD) {
      return undefined;
    }
    const nextId = resolveGroupingIdFromWordQuery(
      groupementAffiliations,
      groupingsSearchQuery,
      groupingsPreferences.searchMode
    );
    if (!nextId || nextId === selectedGroupingId) {
      return undefined;
    }
    handleSelectGrouping(nextId);
    return undefined;
  }, [
    groupementAffiliations,
    groupingsPanelMode,
    groupingsPreferences.searchMode,
    groupingsSearchQuery,
    groupingsSearchScope,
    handleSelectGrouping,
    mode,
    selectedGroupingId,
  ]);

  useEffect(() => () => clearGroupingsSuccessTimer(), [clearGroupingsSuccessTimer]);

  useEffect(() => {
    if (!isRegisterRecencyScreen) {
      setRecencySettingsModalOpen(false);
    }
  }, [isRegisterRecencyScreen]);

  return (
    <GameSessionProvider>
      <>
      <div className={appFit.scaled ? "app-fit app-fit--scaled" : "app-fit app-fit--natural"}>
        <div
          className="app-fit__slot"
          style={
            appFit.scaled
              ? { width: appFit.slotWidth, height: appFit.slotHeight }
              : undefined
          }
        >
          <div
            className="app-fit__inner"
            style={
              appFit.scaled
                ? {
                    width: appFit.innerWidth,
                    height: appFit.innerHeight,
                    transform: `scale(${appFit.scale})`,
                  }
                : undefined
            }
          >
      <div
        className={[
          `custom-pattern mode-${mode}`,
          trainingSessionActive ? 'mode-training--session' : '',
          isRegisterNotesWorkspace ? 'mode-register--notes' : '',
          isRegisterRecencyScreen ? 'mode-register--recency' : '',
          isGroupingsWorkspace ? 'mode-groupings' : '',
          mode === 'quest' && questScreen === 'treasures' ? 'mode-quest--treasures' : '',
          mode === 'quest' && questScreen === 'daily' ? 'mode-quest--journal' : '',
          mode === 'quest' && questScreen === 'titles' ? 'mode-quest--titles' : '',
          mode === 'quest' && questScreen === 'blasons' ? 'mode-quest--blasons' : '',
          mode === 'library' && libraryBookId ? 'mode-library--read' : '',
          isRegisterNotesWorkspace && notesSpread.open ? 'mode-notes-spread' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          appFit.scaled
            ? { width: appFit.frameWidth, height: appFit.frameHeight }
            : textScale > 1
              ? {
                  width: `calc(80vw + ${TEXT_SCALE_FRAME_GROW_X}px)`,
                  maxWidth: `calc(1300px + ${TEXT_SCALE_FRAME_GROW_X}px)`,
                  height: `calc(min(80vh, 100vh - 200px) + ${TEXT_SCALE_FRAME_GROW}px)`,
                  marginTop: `calc(100px - ${TEXT_SCALE_FRAME_GROW / 2}px)`,
                }
              : undefined
        }
      >
        <div className="divsvg"><Arabesque/></div>
        <div className="divsvg2"><Arabesque/></div>
        <div className="divsvg3"><Arabesque extendTail={0.45}/></div>
        <div className="divsvg4"><Arabesque extendTail={0.45}/></div>

        <div className="app-top-menu">
          {isRegisterNotesWorkspace && notesSpread.open ? (
            <>
              {notesEditing ? (
                <div className="notes-spread-edit-tools">
                  <NotesSpreadFontDropdown
                    value={notesSpread.typeface}
                    onChange={(value) => notesControllerRef.current?.setSpreadTypeface?.(value)}
                  />
                  <MenuIconTooltip
                    tooltip={MENU_NOTES_TOOLTIPS.save}
                    placement="below"
                    className={notesDirty ? "icon active notes-spread-save" : "icon notes-spread-save"}
                  >
                    <Save
                      size={50}
                      color="#39210d"
                      strokeWidth={1}
                      onMouseDown={(event) => {
                        if (notesDirty) event.preventDefault();
                      }}
                      onClick={notesDirty ? handleNotesSave : undefined}
                      aria-label="Enregistrer la note"
                      aria-disabled={!notesDirty}
                      style={{ cursor: notesDirty ? "pointer" : "default" }}
                    />
                  </MenuIconTooltip>
                </div>
              ) : null}
              <MenuIconTooltip
                tooltip={MENU_NOTES_TOOLTIPS.closeSpread}
                placement="below"
                className="library-menu-close-tip"
              >
                <button
                  type="button"
                  className="library-menu-close"
                  aria-label={MENU_NOTES_TOOLTIPS.closeSpread}
                  onClick={() => notesControllerRef.current?.closeSpread?.()}
                >
                  <X size={22} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </MenuIconTooltip>
            </>
          ) : (
          <Menu
            key={mode === "library" ? "library-menu" : "main-menu"}
            onChangeMode={handleIconModeChange}
            activeMode={isRegisterRecencyScreen ? null : mode}
            trainingMode={trainingMode}
            registerWorkspaceMode={registerWorkspaceMode}
            onRegisterWorkspaceChange={handleRegisterWorkspaceChange}
            accountMenuMode={accountMenuMode}
            onAccountMenuWorkspaceChange={handleAccountMenuWorkspaceChange}
            libraryAtHome={!libraryBookId}
            onLibraryHome={handleLibraryHome}
            highlightTool={libraryMarkTool}
            onHighlightTool={(kind) =>
              setLibraryMarkTool((current) => (current === kind ? null : kind))
            }
            badgeCounts={{
              medals: notificationBadges.medals,
              packs: notificationBadges.packs,
              packsAlert: notificationBadges.packsAlert,
              cloverRewards: notificationBadges.cloverRewards,
              quests: notificationBadges.quests,
            }}
          />
          )}
        </div>

        {mode === "library" && libraryBookId ? (
          <LibraryReadingTimer />
        ) : null}

        <NotesWorkspaceProvider
          ref={notesControllerRef}
          active={isRegisterNotesWorkspace}
          preferences={notesPreferences}
          searchQuery={notesSearchQuery}
          searchScope={notesSearchScope}
          onMetaChange={handleNotesMetaChange}
        >

        <div className="voletGauche" aria-hidden={leftPanelHidden}>
          <div className='vgauche-ligne1'>
            <div className='vgl1-col1'>
              {mode === 'quest' && questScreen === 'daily' ? (
                <QuestJournalSearchScope
                  value={questJournalSearchScope}
                  onChange={handleQuestJournalSearchScopeChange}
                />
              ) : isRegisterNotesWorkspace && notesSpread.open ? (
                <div className="library-font">
                  <button
                    type="button"
                    className="library-font__button"
                    disabled={notesSpread.fontSize <= LIBRARY_FONT_STEPS[0]}
                    aria-label="Diminuer la taille du texte"
                    onClick={() =>
                      notesControllerRef.current?.setSpreadFont?.(
                        stepLibraryFont(notesSpread.fontSize, -1)
                      )
                    }
                  >
                    A−
                  </button>
                  <button
                    type="button"
                    className="library-font__button library-font__button--plus"
                    disabled={notesSpread.fontSize >= LIBRARY_FONT_STEPS[LIBRARY_FONT_STEPS.length - 1]}
                    aria-label="Augmenter la taille du texte"
                    onClick={() =>
                      notesControllerRef.current?.setSpreadFont?.(
                        stepLibraryFont(notesSpread.fontSize, 1)
                      )
                    }
                  >
                    A+
                  </button>
                </div>
              ) : isRegisterNotesWorkspace ? (
                <QuestJournalSearchScope
                  value={notesSearchScope}
                  onChange={handleNotesSearchScopeChange}
                  name="notes-search-scope"
                  titleTooltip="Rechercher dans le titre de la note"
                  contentTooltip="Rechercher dans le contenu de la note"
                />
              ) : isGroupingsWorkspace ? (
                <QuestJournalSearchScope
                  value={groupingsSearchScope}
                  onChange={handleGroupingsSearchScopeChange}
                  name="groupings-search-scope"
                  options={GROUPINGS_SEARCH_SCOPE_OPTIONS}
                  legend="Mode de recherche des groupements"
                />
              ) : mode === 'library' && libraryBookId ? (
                <LibraryFontSize fontSize={libraryFont} onChange={setLibraryFont} />
              ) : mode === 'library' && !libraryHighlightsOpen ? (
                <QuestJournalSearchScope
                  value={librarySearchScope}
                  onChange={setLibrarySearchScope}
                  name="library-search-scope"
                  legend="Recherche dans la bibliothèque"
                  options={[
                    {
                      id: "author",
                      letter: "A",
                      tooltip: "Recherche par auteur",
                    },
                    {
                      id: "title",
                      letter: "T",
                      tooltip: "Recherche par titre de livre",
                    },
                  ]}
                />
              ) : mode === 'library' ? null : (
                <MenuLeft
                  mode={mode}
                  activeFilter={isRegisterRecencyScreen ? 'info' : activeFilter}
                  showFilter={!isRegisterNotesWorkspace && !isGroupingsWorkspace}
                  showPacks={false}
                  showRecencySettings={isRegisterRecencyScreen}
                  recencySettingsActive={recencySettingsModalOpen}
                  filterSubActive={
                    isRegisterRecencyScreen
                      ? isRegisterFilterActive(registerFilters)
                      : activeFilter === 'success'
                      ? successEwssFilter !== SUCCESS_EWSS_FILTERS.ALL
                      : activeFilter === 'info'
                      ? isRegisterFilterActive(registerFilters)
                      : false
                  }
                  onFilterClick={() => {
                    if (isRegisterRecencyScreen || activeFilter === 'info') {
                      setRegisterFilterModalOpen(true);
                      return;
                    }
                    if (activeFilter === 'success') {
                      setSuccessFilterModalOpen(true);
                    }
                  }}
                  onFilterClear={() => {
                    if (isRegisterRecencyScreen || activeFilter === 'info') {
                      setRegisterFilters({ ...DEFAULT_REGISTER_FILTERS });
                      setRegisterFilterModalOpen(false);
                      return;
                    }
                    if (activeFilter === 'success') {
                      setSuccessEwssFilter(SUCCESS_EWSS_FILTERS.ALL);
                      setSuccessFilterModalOpen(false);
                    }
                  }}
                  onRecencySettingsClick={() => setRecencySettingsModalOpen(true)}
                />
              )}
            </div>
            <div className='vgl1-col2'>
              {isRegisterWordsWorkspace ? (
                <SearchBar value={searchQuery} onSearch={setSearchQuery} />
              ) : null}
              {mode === 'library' && libraryBookId ? (
                <LibraryProgress
                  page={libraryProgress.page}
                  total={libraryProgress.total}
                  pending={!libraryProgress.shown}
                  onSeek={setLibrarySeek}
                />
              ) : mode === 'library' && libraryHighlightKind ? (
                <SearchBar
                  value={libraryHighlightQuery}
                  onSearch={setLibraryHighlightQuery}
                  inputAriaLabel={
                    libraryHighlightKind === "word"
                      ? "Chercher un mot à capturer"
                      : libraryHighlightKind === "favorite"
                        ? "Chercher un passage favori"
                        : "Chercher un passage important"
                  }
                  placeholder={
                    libraryHighlightKind === "word" ? "chercher un mot" : "chercher un passage"
                  }
                />
              ) : mode === 'library' ? (
                <SearchBar
                  value={libraryQuery}
                  onSearch={setLibraryQuery}
                  inputAriaLabel={
                    librarySearchScope === "author"
                      ? "Rechercher un auteur"
                      : "Rechercher un livre"
                  }
                  placeholder={
                    librarySearchScope === "author" ? "cherchez un auteur…" : "cherchez un livre…"
                  }
                />
              ) : null}
              {isGroupingsWorkspace ? (
                <SearchBar
                  value={groupingsSearchQuery}
                  onSearch={setGroupingsSearchQuery}
                  inputAriaLabel={
                    groupingsSearchScope === GROUPINGS_SEARCH_SCOPES.WORD
                      ? "Rechercher un mot affilié à un groupement"
                      : "Rechercher un groupement thématique"
                  }
                  placeholder={
                    groupingsSearchScope === GROUPINGS_SEARCH_SCOPES.WORD
                      ? "tapez un mot"
                      : "tapez un groupement"
                  }
                />
              ) : null}
              {mode === 'quest' && questScreen === 'daily' ? (
                <SearchBar
                  value={questJournalSearchQuery}
                  onSearch={handleQuestJournalSearchChange}
                  inputAriaLabel={
                    questJournalSearchScope === 'content'
                      ? "Rechercher dans le contenu d'une quête"
                      : "Rechercher dans le titre d'une quête"
                  }
                />
              ) : null}
              {isRegisterNotesWorkspace && notesSpread.open ? (
                <LibraryProgress
                  page={notesSpread.page}
                  total={notesSpread.total}
                  onSeek={(page) => notesControllerRef.current?.seekSpread?.(page)}
                />
              ) : isRegisterNotesWorkspace ? (
                <SearchBar
                  value={notesSearchQuery}
                  onSearch={handleNotesSearchChange}
                  inputAriaLabel={
                    notesSearchScope === NOTES_SEARCH_SCOPES.CONTENT
                      ? "Rechercher dans le contenu d'une note"
                      : "Rechercher dans le titre d'une note"
                  }
                />
              ) : null}
            </div>
          </div>
          <div className='vgauche-ligne2'>
            {isRegisterWordsWorkspace ? (
              <WordListBox
                selectedWord={selectedWord}
                onSelectWord={handleSelectWord}
                onWordsLoaded={handleWordsLoaded}
                onCountChange={handleCountChange}
                filter={searchQuery}
                activeFilter={isRegisterRecencyScreen ? 'info' : activeFilter}
                successEwssFilter={successEwssFilter}
                registerFilters={registerFilters}
                registerPreferences={
                  isRegisterRecencyScreen ? recencyPreferences : registerPreferences
                }
                refreshKey={listRefresh}
                keyboardNavEnabled={!isRegisterRecencyScreen}
                active={!isRegisterRecencyScreen}
                filterSelectionRef={filterSelectionRef}
                onFilteredEntriesChange={handleFilteredEntriesChange}
                onLoadingChange={handleRegisterListLoadingChange}
              />
            ) : null}
            {isRegisterNotesWorkspace ? <NotesListPanel /> : null}
            {isGroupingsWorkspace ? (
              <GroupingsListPanel
                groupings={visibleGroupings}
                selectedGroupingId={
                  groupingsPanelMode === "create" ? null : selectedGroupingId
                }
                onSelectGrouping={handleSelectGrouping}
                loading={showGroupingsListSkeleton}
                scrollStorageKey={getGroupingsScrollStorageKey(
                  `${groupingsSearchScope}:${normalizeRegisterSearch(groupingsSearchQuery)}`,
                  groupingsPreferences.searchMode
                )}
                keyboardNavEnabled={
                  groupingsPanelMode !== "create" &&
                  !groupingsSettingsOpen &&
                  !groupingsConfirm &&
                  !showGroupingsListSkeleton
                }
              />
            ) : null}
            <div className="arb3"><Arabesque3/></div>
          </div>
          <div className='vgauche-ligne3' />
        </div>

        <div
          className="voletMenuMilieu"
          aria-hidden={
            (!isRegisterWordsWorkspace && !isRegisterNotesWorkspace && !isGroupingsWorkspace) ||
            isRegisterRecencyScreen
          }
        >
          {isRegisterNotesWorkspace ? (
            <MenuNotesMiddle
              visible={!notesSpread.open}
              isEditing={notesEditing}
              hasNote={notesHasNote}
              canSave={notesDirty}
              settingsActive={notesSettingsModalOpen}
              onRead={handleNotesRead}
              onEdit={handleNotesEdit}
              onSave={handleNotesSave}
              onDelete={handleNotesDelete}
              onSettingsClick={() => setNotesSettingsModalOpen(true)}
            />
          ) : isGroupingsWorkspace ? (
            <MenuGroupingsMiddle
              visible
              settingsActive={groupingsSettingsOpen}
              onCreate={handleStartGroupingCreate}
              onSettingsClick={() => setGroupingsSettingsOpen(true)}
            />
          ) : isRegisterRecencyScreen ? null : (
          <MenuMiddle
              mode={isRegisterWordsWorkspace ? 'register' : 'notes'}
            activeFilter={activeFilter}
            activeAction={activeAction}
            onActionChange={handleActionChange}
              settingsActive={registerSettingsModalOpen}
              onSettingsClick={() => setRegisterSettingsModalOpen(true)}
          />
          )}
        </div>

        <div className="voletDroite">
          <div className="vdroite-ligne1" aria-hidden="true" />

          <div className='vdroite-ligne2'>
            {!isRegisterRecencyScreen ? (
              <>
            <div className="arabesque2-d1"><Arabesque2/></div>
            <div className="arabesque2-d2"><Arabesque2/></div>
            <div className="arabesque2-d3"><Arabesque2/></div>
            <div className="arabesque2-d4"><Arabesque2/></div>
              </>
            ) : null}

            <div className="vdroite-content" style={{ width: '100%' }}>
              {isRegisterWordsWorkspace && (
                <div
                  className={[
                    "containerDetailMot-shell",
                    detailScrollFades.top && "containerDetailMot-shell--fade-top",
                    detailScrollFades.bottom && "containerDetailMot-shell--fade-bottom",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div
                    ref={detailScrollRef}
                    className="containerDetailMot detail-scroll"
                    onScroll={handleDetailScroll}
                  >
                    <div ref={detailContentRef} className="containerDetailMot__content">
                      {activeAction === "recency" ? (
                        <RegisterRecencyPanel
                          activeStratum={recencyStratum}
                          onStratumChange={handleRecencyStratumChange}
                          entries={registerFilteredEntries}
                          listDisplayMode={recencyPreferences.listDisplayMode}
                          showColorCode={recencyPreferences.showColorCode}
                          onSelectWord={handleSelectWord}
                        />
                      ) : activeAction === "lastGameSummary" && activeFilter === "lastGame" ? (
                        <LastGameStatsPanel
                          lastSession={listCountMeta?.lastSession}
                          wordInsights={listCountMeta?.wordInsights}
                        />
                      ) : activeAction === "filteredStats" && activeFilter !== "lastGame" ? (
                        <RegisterFilteredStatsPanel entries={registerFilteredEntries} />
                      ) : activeAction === "stats" ? (
                        <WordDetailsPanel
                          mot={selectedWord}
                          onListRefresh={handleListRefresh}
                          forceSkeleton={registerListLoading}
                        />
                      ) : (
                        <WordEditPanel
                          mot={selectedWord}
                          onListRefresh={handleListRefresh}
                          forceSkeleton={registerListLoading}
                          onWordRenamed={handleWordRenamed}
                          onWordDeleted={() => {
                            setSelectedWord(null);
                            setSelectedSide(null);
                            handleListRefresh();
                          }}
                        />
                      )}
                    </div>
                  </div>
              </div>
            )}
              {isRegisterNotesWorkspace ? <NotesDetailPanel /> : null}
              {isGroupingsWorkspace ? (
                groupingsPanelMode === "create" ? (
                  <GroupingCreatePanel
                    key={groupingsCreateKey}
                    usedColors={usedGroupementColors}
                    onCancel={handleCancelGroupingCreate}
                    onCreated={handleGroupingCreated}
                  />
                ) : groupingsPanelMode === "success" ? (
                  <GroupingSuccessPanel title={groupingsSuccessTitle} />
                ) : (
                  <GroupingDetailPanel
                    grouping={selectedGrouping}
                    words={selectedGroupingWords}
                    loading={showGroupingsDetailSkeleton}
                    listDisplayMode={groupingsPreferences.listDisplayMode}
                    showColorCode={groupingsPreferences.showColorCode}
                    detailPane={groupingsPreferences.detailPane}
                    onDetailPaneChange={(pane) =>
                      setGroupingsPreferences((prev) => ({ ...prev, detailPane: pane }))
                    }
                    usedColors={groupings
                      .filter((item) => item.groupementId !== selectedGroupingId)
                      .map((item) => item.color)}
                    onRename={handleRenameGrouping}
                    onRequestDelete={() => setGroupingsConfirm("delete")}
                    onRequestEmpty={() => setGroupingsConfirm("empty")}
                    onRequestRemoveWords={() => setGroupingsConfirm("remove")}
                    onRequestReassignWords={() => setGroupingsConfirm("reassign")}
                    canReassign={groupings.length > 1}
                  />
                )
              ) : null}
              {mode === 'quest' && (
                <div className="questView">
                  {questScreen === 'daily' ? (
                    <QuestView
                      playerName={questPlayerName}
                      searchQuery={questJournalSearchQuery}
                      searchScope={questJournalSearchScope}
                      journalDetailScrollAnchor={questPreferences.journalDetailScrollAnchor}
                      completedObjectiveDisplay={questPreferences.completedObjectiveDisplay}
                      onCountdownChange={setQuestCountdown}
                      onNavigateToTreasures={(focus) => {
                        if (focus?.collectionId && focus?.relicId) {
                          setQuestTreasureFocus(focus);
                        }
                        setQuestScreen('treasures');
                      }}
                    />
                  ) : questScreen === 'titles' ? (
                    <RadiantDayTitlesView
                      playerName={questPlayerName}
                      profilePortraitMode={questPreferences.profilePortraitMode}
                    />
                  ) : questScreen === 'blasons' ? (
                    <RadiantDayBlasonsView />
                  ) : (
                    <QuestTreasureHallView
                      focus={questTreasureFocus}
                      onFocusApplied={() => setQuestTreasureFocus(null)}
                    />
                  )}
                </div>
              )}
              {mode === 'game' && (
                <div className="gameView">
                  <GameQCM onContinueToMastery={handleGameContinueToMastery} />
                </div>
              )}
              {mode === 'capture' && (
                <div className="captureView">
                  <CaptureDeMot
                    embedded
                    panel={capturePanel}
                    preparedCapture={captureDraft}
                    afterCaptureBehavior={capturePreferences.afterCaptureBehavior}
                    onClose={() => handleIconModeChange('register')}
                  />
                </div>
              )}
              {mode === 'training' && (
                <div className="trainingView">
                  <EntrainementVoc
                    embedded
                    onClose={() => handleIconModeChange('register')}
                    onPhaseChange={setTrainingPhase}
                    onRegisterStopSession={registerTrainingStopSession}
                    trainingMode={trainingMode}
                    onTrainingModeChange={setTrainingMode}
                    onQuestTrainingAvailableChange={setQuestTrainingAvailable}
                  />
                </div>
              )}
              {mode === 'stats' && (
                <div className="statsView">
                  <GameStatsView
                    screen={statsScreen}
                    onViewSessionPool={handleViewSessionPool}
                  />
                </div>
              )}
              {mode === 'cpanel' && user?.admin && (
                <div className="cpanelView">
                  <ControlPanelView screen={cpanelScreen} />
                </div>
              )}
              {mode === 'collection' && (
                <div className="collectionView">
                  <GemCollectionView
                    activeTab={collectionTab}
                    cloverRewardsUnread={notificationBadges.cloverRewards}
                  />
                </div>
              )}
              {mode === 'medals' && (
                <div
                  className={`medalsView${
                    masteryScreen === 'history' ? ' medalsView--history' : ''
                  }`}
                >
                  <MasteryProgressView
                    screen={masteryScreen}
                    onUnreadHistoryCountChange={refreshNotificationBadges}
                  />
                </div>
              )}
              {mode === 'register' && (
                <div className="registerView" />
              )}
              {mode === 'library' && (
                <LibraryView
                  query={libraryQuery}
                  searchScope={librarySearchScope}
                  openedBookId={libraryBookId}
                  onOpenBook={handleOpenLibraryBook}
                  fontSize={libraryFont}
                  seekPage={librarySeek}
                  onSeekApplied={handleLibrarySeekApplied}
                  onProgress={handleLibraryProgress}
                  reloadToken={libraryReload}
                  favoritesOnly={libraryFavoritesOnly}
                  markTool={libraryMarkTool}
                  charactersToken={libraryCharactersToken}
                  searchToken={librarySearchToken}
                  highlightsOpen={libraryHighlightsOpen}
                  highlightQuery={libraryHighlightQuery}
                  highlightResume={libraryResume}
                  onHighlightResumeApplied={handleHighlightResumeApplied}
                  onHighlightKindChange={handleHighlightKindChange}
                  onCaptureWord={handleCaptureHighlightedWord}
                  onCloseHighlights={() => {
                    setLibraryHighlightsOpen(false);
                    setLibraryHighlightQuery("");
                    setLibraryResume(null);
                  }}
                  onToggleFavorites={() => setLibraryFavoritesOnly((value) => !value)}
                  onResumeBookChange={setLibraryResumeBook}
                />
              )}
            </div>
          </div>

          <LibraryImportNotice
            open={Boolean(libraryImportNotice)}
            ok={Boolean(libraryImportNotice?.ok)}
            duplicate={Boolean(libraryImportNotice?.duplicate)}
            title={libraryImportNotice?.title || ""}
            detail={libraryImportNotice?.detail || ""}
            onClose={handleLibraryImportNoticeClose}
          />

          <div className='vdroite-ligne3'>
            {isRegisterWordsWorkspace && activeFilter === 'info' && (
              <span className="compteurMots">
                {registerListLoading ? (
                  <span className="register-skel register-skel--footer" />
                ) : (
                  <>
                    {listCount} {listCount > 1 ? 'mots enregistrés' : 'mot enregistré'}
                  </>
                )}
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'favorites' && (
              <span className="compteurMots">
                {listCount} {listCount > 1 ? 'mots favoris' : 'mot favori'}
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'priority' && (
              <span className="compteurMots">
                {listCount} {listCount > 1 ? 'mots prioritaires' : 'mot prioritaire'}
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'neverPlayed' && listCountMeta && (
              <span className="compteurMots">
                {listCountMeta.count} / {listCountMeta.totalWords}{" "}
                {listCountMeta.count > 1 ? 'mots non joués' : 'mot non joué'} (
                {formatPercent(listCountMeta.percent)} %)
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'success' && listCountMeta && (
              <span className="compteurMots">
                {listCountMeta.count}{" "}
                {listCountMeta.count > 1
                  ? 'mots correctement restitués'
                  : 'mot correctement restitué'}{' '}
                ({formatPercent(listCountMeta.percent)} %)
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'failures' && listCountMeta && (
              <span className="compteurMots">
                {listCountMeta.count}{" "}
                {listCountMeta.count > 1
                  ? 'mots non correctement restitués'
                  : 'mot non correctement restitué'}{' '}
                ({formatPercent(listCountMeta.percent)} %)
              </span>
            )}
            {isRegisterWordsWorkspace && activeFilter === 'lastGame' && listCountMeta && (
              <span className="compteurMots">
                {listCountMeta.count}{" "}
                {listCountMeta.count > 1
                  ? 'mots de la dernière partie'
                  : 'mot de la dernière partie'}
              </span>
            )}
            {trainingSessionActive ? <TrainingShortcutsLegend /> : null}
            {mode === 'game' && <GameDifficultyFooter />}
            {isRegisterNotesWorkspace && !notesSpread.open ? (
              <NotesCategoryFilterDropdown
                value={notesPreferences.categoryFilter}
                onChange={handleNotesCategoryFilterChange}
              />
            ) : null}
            {isGroupingsWorkspace ? (
              <span className="compteurMots groupings-footer-stats">
                {showGroupingsListSkeleton ? (
                  <>
                    <span className="groupings-footer-stats__label" aria-hidden="true">
                      <span className="groupings-skel-metrics">
                        00 groupements thématiques
                      </span>
                      <span className="register-skel groupings-skel--footer-label" />
                    </span>
                    <span
                      className="groupings-footer-stats__pill register-skel"
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <>
                    <span>
                      {visibleGroupings.length}{" "}
                      {visibleGroupings.length > 1
                        ? "groupements thématiques"
                        : "groupement thématique"}
                    </span>
                    <span
                      className="groupings-footer-stats__pill"
                      aria-label={`${visibleGroupingsWordCount.toLocaleString("fr-FR")} ${
                        visibleGroupingsWordCount === 1 ? "mot" : "mots"
                      }`}
                    >
                      {visibleGroupingsWordCount.toLocaleString("fr-FR")}{" "}
                      {visibleGroupingsWordCount === 1 ? "mot" : "mots"}
                    </span>
                  </>
                )}
              </span>
            ) : null}
            {mode === 'quest' && questScreen === 'daily' && questCountdown?.value ? (
              <span className="compteurMots quest-view-countdown" aria-live="polite">
                {(questCountdown.todayCompleted
                  ? 'Prochaine quête dans'
                  : 'Temps restant') + ' : '}
                <strong>{questCountdown.value}</strong>
              </span>
            ) : null}
          </div>
        </div>

        <div
          className="voletMenuDroite"
          aria-hidden={
            mode !== 'register'
            && mode !== 'groupings'
            && mode !== 'collection'
            && mode !== 'game'
            && mode !== 'medals'
            && mode !== 'stats'
            && mode !== 'capture'
            && mode !== 'quest'
            && mode !== 'training'
            && !(mode === 'library' && !libraryBookId)
          }
        >
            <MenuFilter
            visible={isRegisterWordsWorkspace}
            activeFilter={isRegisterRecencyScreen ? null : activeFilter}
            onFilter={handleFilterChange}
          />
          <LibraryImportNav
            visible={mode === 'library' && !libraryBookId}
            onImport={handleLibraryImport}
            highlightsOpen={libraryHighlightsOpen}
            onToggleHighlights={() => setLibraryHighlightsOpen((value) => !value)}
            resumeBook={libraryResumeBook}
            onResume={handleOpenLibraryBook}
          />
          <MenuNotesNav
            visible={isRegisterNotesWorkspace && !notesSpread.open}
            canCreate={!notesEditing}
            canExpand={notesHasNote}
            expandEditing={notesEditing}
            onCreate={handleNotesCreate}
            onExpand={() => notesControllerRef.current?.openSpread?.()}
          />
          <MenuQuestNav
            visible={mode === 'quest'}
            screen={questScreen}
            onScreenChange={setQuestScreen}
            onSettingsClick={() => setQuestSettingsModalOpen(true)}
          />
          <TrainingModeNav
            visible={trainingSetupActive}
            mode={trainingMode}
            questSessionAvailable={questTrainingAvailable}
            onModeChange={setTrainingMode}
          />
          <TrainingSessionNav
            visible={trainingSessionActive}
            onConfigClick={handleTrainingConfigClick}
          />
          <MenuCollectionNav
            visible={mode === 'collection'}
            activeTab={collectionTab}
            onTabChange={handleCollectionTabChange}
            cloverRewardsUnread={notificationBadges.cloverRewards}
          />
          <MenuMasteryNav
            visible={mode === 'medals'}
            screen={masteryScreen}
            onScreenChange={setMasteryScreen}
            unreadHistoryCount={
              masteryScreen === 'history' ? 0 : notificationBadges.history
            }
          />
          <MenuStatsNav
            visible={mode === 'stats'}
            screen={statsScreen}
            onScreenChange={setStatsScreen}
          />
          <MenuCpanelNav
            visible={mode === 'cpanel' && Boolean(user?.admin)}
            screen={cpanelScreen}
            onScreenChange={setCpanelScreen}
          />
          <GamePrepNav />
          <MenuCaptureNav
            visible={mode === 'capture'}
            activePanel={capturePanel}
            onPanelChange={setCapturePanel}
            onSettingsClick={() => setCaptureSettingsModalOpen(true)}
            />
        </div>
        </NotesWorkspaceProvider>
        <LibraryLauncher
          active={mode === 'library'}
          reading={mode === 'library' && Boolean(libraryBookId)}
          onOpen={() =>
            handleIconModeChange(mode === 'library' ? 'register' : 'library')
          }
          onOpenCharacters={() => setLibraryCharactersToken((current) => current + 1)}
        />
        <KingdomCitizens reading={mode === 'library' && Boolean(libraryBookId)} />
        <KingdomMessaging />
        <PlayerMfaPanel
          reading={mode === 'library' && Boolean(libraryBookId)}
          onOpenSearch={() => setLibrarySearchToken((count) => count + 1)}
        />
        <AppCredit />
      </div>
          </div>
        </div>
      </div>
      <div id="app-portal-root" className="app-portal-root" />

      <SuccessEwssFilterModal
        open={successFilterModalOpen && mode === 'register' && activeFilter === 'success'}
        value={successEwssFilter}
        onChange={setSuccessEwssFilter}
        onClose={() => setSuccessFilterModalOpen(false)}
      />

      <RegisterFilterModal
        open={
          registerFilterModalOpen &&
          isRegisterWordsWorkspace &&
          (activeFilter === 'info' || isRegisterRecencyScreen)
        }
        value={registerFilters}
        onChange={setRegisterFilters}
        onClose={() => setRegisterFilterModalOpen(false)}
      />

      <RegisterSettingsModal
        open={registerSettingsModalOpen && isRegisterWordsWorkspace && !isRegisterRecencyScreen}
        value={registerPreferences}
        onChange={setRegisterPreferences}
        onClose={() => setRegisterSettingsModalOpen(false)}
      />

      <RegisterSettingsModal
        open={recencySettingsModalOpen && isRegisterRecencyScreen}
        value={recencyPreferences}
        onChange={(next) => setRecencyPreferences(sanitizeRegisterRecencyPreferences(next))}
        onClose={() => setRecencySettingsModalOpen(false)}
        title="Paramètres de la récence"
        showScrollPreferences={false}
        namePrefix="recency"
        defaults={DEFAULT_REGISTER_RECENCY_PREFERENCES}
      />

      <RegisterSettingsModal
        open={groupingsSettingsOpen && isGroupingsWorkspace}
        value={groupingsPreferences}
        onChange={(next) => setGroupingsPreferences(sanitizeGroupingsPreferences(next))}
        onClose={() => setGroupingsSettingsOpen(false)}
        title="Préférences d'affichage"
        showScrollPreferences={false}
        gothicTitle
        searchHelpMap={GROUPINGS_SEARCH_MODE_HELP}
        namePrefix="groupings"
        defaults={DEFAULT_GROUPINGS_PREFERENCES}
      />

      <GroupingConfirmModal
        open={groupingsConfirm === "delete" && Boolean(selectedGrouping)}
        title="Suppression d'un groupement thématique"
        danger
        busy={groupingsConfirmBusy}
        onConfirm={handleDeleteGrouping}
        onClose={() => {
          if (!groupingsConfirmBusy) setGroupingsConfirm(null);
        }}
      >
        <p>
          Le groupement thématique{" "}
          {selectedGrouping ? `« ${selectedGrouping.title} »` : ""} sera
          définitivement supprimé. Les mots qui y sont affiliés ne seront pas
          effacés du registre : ils n&apos;appartiendront simplement plus à
          aucun groupement.
        </p>
      </GroupingConfirmModal>

      <GroupingConfirmModal
        open={groupingsConfirm === "empty" && Boolean(selectedGrouping)}
        title="Vidage d'un groupement thématique"
        busy={groupingsConfirmBusy}
        onConfirm={handleEmptyGrouping}
        onClose={() => {
          if (!groupingsConfirmBusy) setGroupingsConfirm(null);
        }}
      >
        <p>
          Le groupement thématique{" "}
          {selectedGrouping ? `« ${selectedGrouping.title} »` : ""} sera
          conservé, mais tous les mots qui y sont affiliés en seront retirés.
          Les mots resteront dans le registre.
        </p>
      </GroupingConfirmModal>

      <GroupingRemoveWordsModal
        open={groupingsConfirm === "remove" && Boolean(selectedGrouping)}
        words={selectedGroupingWords}
        busy={groupingsConfirmBusy}
        onConfirm={handleRemoveWordsFromGrouping}
        onClose={() => {
          if (!groupingsConfirmBusy) setGroupingsConfirm(null);
        }}
      />

      <GroupingReassignWordsModal
        open={groupingsConfirm === "reassign" && Boolean(selectedGrouping)}
        words={selectedGroupingWords}
        groupements={groupings}
        sourceGroupementId={selectedGrouping?.groupementId || ""}
        busy={groupingsConfirmBusy}
        onConfirm={handleReassignWordsFromGrouping}
        onClose={() => {
          if (!groupingsConfirmBusy) setGroupingsConfirm(null);
        }}
      />

      <NotesSettingsModal
        open={notesSettingsModalOpen && isRegisterNotesWorkspace}
        value={notesPreferences}
        onChange={handleNotesPreferencesChange}
        onClose={() => setNotesSettingsModalOpen(false)}
      />

      <CaptureSettingsModal
        open={captureSettingsModalOpen && mode === 'capture'}
        value={capturePreferences}
        onChange={setCapturePreferences}
        onClose={() => setCaptureSettingsModalOpen(false)}
      />

      <QuestSettingsModal
        open={questSettingsModalOpen && mode === 'quest'}
        preferences={questPreferences}
        onPreferencesChange={handleQuestPreferencesChange}
        onClose={() => setQuestSettingsModalOpen(false)}
      />
      </>
    </GameSessionProvider>
  );
}

export default App;
